import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Download } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import Reveal from "@/components/Reveal";
import ArticleRow from "@/components/ArticleRow";
import CTASection from "@/components/CTASection";
import NotFound from "@/pages/NotFound";
import { CATEGORY_PAGES, categoryPageBySlug } from "@/lib/categories";

const clampStyle = { display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" };

export default function CategoryPage() {
  const { slug } = useParams();
  const { lang, tr } = useLang();
  const t = tr("common");
  const [articles, setArticles] = useState(undefined);
  const page = categoryPageBySlug(slug);
  const key = page?.key;

  useEffect(() => {
    if (!page) return;
    const meta = page.seo[lang];
    const url = `${window.location.origin}/categoria/${page.slug}`;
    document.title = meta.title;
    const setMeta = (attr, name, content) => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("name", "description", meta.desc);
    setMeta("property", "og:title", meta.title);
    setMeta("property", "og:description", meta.desc);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", `${window.location.origin}/api/og/brand`);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:image", `${window.location.origin}/api/og/brand`);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);
  }, [page, lang]);

  useEffect(() => {
    if (!key) return;
    setArticles(undefined);
    api
      .get("/articles", { params: { category: key } })
      .then((r) => setArticles(r.data || []))
      .catch(() => setArticles([]));
  }, [key]);

  if (!page) return <NotFound />;

  return (
    <>
      <section className="relative overflow-hidden bg-navy px-6 pb-16 pt-36 noise lg:px-10 lg:pb-24 lg:pt-44">
        <div className="pointer-events-none absolute right-0 top-0 h-[420px] w-[420px] rounded-full bg-gold/10 blur-[130px]" />
        <div className="relative mx-auto max-w-7xl">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-gold">
              <span className="h-px w-10 bg-gold/70" /> {t.catEyebrow}
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <h1 className="mt-6 max-w-3xl font-serif text-4xl leading-[1.1] tracking-tight text-ivory sm:text-5xl lg:text-6xl" data-testid="category-page-title">
              {page.h1[lang]}
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ivory/60" data-testid="category-page-intro">
              {page.intro[lang]}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="min-h-[40vh] bg-navy-light px-6 py-14 lg:px-10 lg:py-20">
        <div className="mx-auto max-w-7xl">
          {articles === undefined ? (
            <p className="py-16 text-center font-mono text-xs uppercase tracking-[0.25em] text-ivory/40">{t.loading}</p>
          ) : articles.length === 0 ? (
            <p className="border-t border-gold/15 py-16 text-center text-sm text-ivory/50" data-testid="category-page-empty">
              {t.noResults}
            </p>
          ) : (
            <div data-testid="category-articles-list">
              {articles.map((a, i) => (
                <Reveal key={a.id || a.slug} delay={Math.min(i * 0.06, 0.3)}>
                  <ArticleRow article={a} index={i} />
                </Reveal>
              ))}
            </div>
          )}

          {page.key === "INCOTERMS" && (
            <Link
              to="/guida-incoterms"
              data-testid="guide-banner"
              className="group mt-12 flex flex-col items-start justify-between gap-4 border border-gold/30 bg-navy p-7 transition-colors hover:border-gold/60 sm:flex-row sm:items-center"
            >
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{t.guideEyebrow}</p>
                <p className="mt-2 font-serif text-xl text-ivory sm:text-2xl">{t.guideBanner}</p>
              </div>
              <span className="inline-flex items-center gap-2 bg-gold px-6 py-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-navy transition-colors group-hover:bg-gold-bright">
                <Download className="h-3.5 w-3.5" /> {t.guideCta}
              </span>
            </Link>
          )}

          <div className="mt-20" data-testid="category-pages-nav">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{t.exploreCategories}</p>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {CATEGORY_PAGES.map((p) => (
                <Link
                  key={p.slug}
                  to={`/categoria/${p.slug}`}
                  data-testid={`category-link-${p.slug}`}
                  className={`group flex h-full flex-col border p-6 transition-colors ${
                    p.key === page.key ? "border-gold/60 bg-gold/[0.07]" : "border-gold/15 bg-white/[0.02] hover:border-gold/50"
                  }`}
                >
                  <p className="font-serif text-xl text-ivory transition-colors group-hover:text-gold-bright">{p.label[lang]}</p>
                  <p className="mt-3 text-sm leading-relaxed text-ivory/50" style={clampStyle}>
                    {p.intro[lang]}
                  </p>
                  <span className="mt-auto inline-flex items-center gap-2 pt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                    {t.viewArticles}
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
